import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, ShieldCheck, BadgeCheck, Download } from "lucide-react";
import SEO from "@/components/SEO";
import MeshCanvas from "@/components/MeshCanvas";
import Marquee from "@/components/Marquee";
import SystemDiagram from "@/components/SystemDiagram";
import ProductCard from "@/components/ProductCard";
import CTASection from "@/components/CTASection";
import { Reveal, MaskedLine, Overline } from "@/components/Reveal";
import { BuildingMesh, SmartControl, MeshPulse } from "@/components/Vectors";
import { CAPABILITIES, WHY, APPLICATION_CARDS } from "@/data/content";
import { useCatalog, getById } from "@/lib/catalog";
import { CAPABILITY_ICONS, WHY_ICONS, APP_ICONS, ECO_ICONS } from "@/lib/icons";
import { track } from "@/lib/api";

const ECOSYSTEM = [
  { cat: "Indoor", label: "Indoor Controls", desc: "OcuSense and PIR fixture and ceiling controls for office, low-bay and high-bay.", ids: ["06901", "06906", "06936"] },
  { cat: "Node", label: "Nodes & Power Packs", desc: "BLE nodes and UL Listed power pack nodes with 20A relays and 0–10V output.", ids: ["06913", "06934", "06935"] },
  { cat: "Outdoor", label: "Outdoor Controls", desc: "IP65 outdoor PIR controls with photocell, rated up to 40 ft mounting.", ids: ["06921", "06922"] },
  { cat: "Wall Switch", label: "Wall Controls", desc: "PIR wall switches and self-powered BLE switches — no wire, no battery.", ids: ["06951", "06952", "06953"] },
  { cat: "Wall Outlet", label: "Wall Outlet", desc: "AC powered BLE wall outlet for controlled receptacles.", ids: ["06954"] },
  { cat: "Long Range Extender", label: "Network Extension", desc: "Long Range Extenders push Bluetooth Mesh reach up to 328 ft.", ids: ["06970", "06971"] },
];

const Circled = ({ children }) => (
  <span className="relative inline-block whitespace-nowrap px-2">
    <span className="relative z-10">{children}</span>
    <svg
      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[122%] h-[160%] overflow-visible"
      viewBox="0 0 220 90"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <motion.ellipse
        cx="110" cy="45" rx="106" ry="41"
        fill="none" stroke="#6FA82C" strokeWidth="3.5" strokeLinecap="round"
        transform="rotate(-2 110 45)"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 1.1, delay: 1.15, ease: "easeInOut" }}
      />
    </svg>
  </span>
);

const Chapter = ({ n, children }) => (
  <div className="flex items-baseline gap-4 mb-4">
    <span className="font-mono text-sm text-signal">/{n}</span>
    <Overline>{children}</Overline>
  </div>
);

export default function Home() {
  const { products } = useCatalog();
  const featured = ["06934", "06905", "06937", "06952"].map((id) => getById(products, id)).filter(Boolean);

  return (
    <div data-testid="home-page">
      <SEO path="/" />

      {/* HERO */}
      <section className="relative min-h-screen min-h-[100svh] flex items-end overflow-hidden bg-paper" data-testid="hero">
        <div className="absolute inset-0 grid-lines" aria-hidden="true" />
        <MeshCanvas className="absolute inset-0 w-full h-full" density={80} />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "linear-gradient(to top, var(--paper) 8%, transparent 55%), linear-gradient(to bottom, var(--paper) 0%, transparent 40%)" }}
          aria-hidden="true"
        />
        <motion.div
          className="absolute right-0 bottom-0 top-16 hidden lg:flex items-center w-[46%] pointer-events-none"
          aria-hidden="true"
          animate={{ y: [0, -12, 0] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
        >
          <BuildingMesh className="w-full h-auto max-h-[72%] opacity-90" />
        </motion.div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pb-24 pt-40 w-full">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15, duration: 0.8 }}
            className="font-mono text-xs uppercase tracking-[0.3em] text-signal mb-8"
            data-testid="hero-overline"
          >
            Amaya Pro — Network Lighting Control
          </motion.p>
          <h1 className="font-display font-bold tracking-tighter leading-[0.98] text-[13vw] sm:text-7xl lg:text-8xl" data-testid="hero-headline">
            <MaskedLine delay={0.25}>Intelligent lighting</MaskedLine>
            <MaskedLine delay={0.37}>control for</MaskedLine>
            <MaskedLine delay={0.49}><span className="text-signal">smarter buildings.</span></MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            className="text-fog text-base md:text-lg max-w-xl mt-8 leading-relaxed"
            data-testid="hero-sub"
          >
            Connect lighting, sensors and controls through a secure Bluetooth Mesh network — and manage lighting with intelligent automation, scheduling, sensing and energy monitoring.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.7 }}
            className="flex flex-wrap gap-4 mt-10"
          >
            <Link
              to="/contact?type=demo"
              data-testid="hero-demo-btn"
              onClick={() => track("cta_click", { cta: "request_demo", location: "hero" })}
              className="px-7 py-3.5 bg-signal text-white font-medium hover:bg-[#5c8f23] transition-colors flex items-center gap-2"
            >
              Request a Demo <ArrowRight size={16} />
            </Link>
            <Link
              to="/products"
              data-testid="hero-products-btn"
              className="px-7 py-3.5 border border-zinc-300 text-ink hover:border-signal transition-colors"
            >
              Explore Products
            </Link>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <Marquee items={["Occupancy Sensing", "Daylight Harvesting", "Bluetooth Mesh", "Energy Monitoring", "High-End Trim", "Remote Management", "Scheduling"]} />

      {/* 01 — WHAT IS AMAYA */}
      <section className="bg-white py-24 md:py-32" data-testid="section-what">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <Chapter n="01">What is Amaya</Chapter>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] max-w-3xl">
              One connected system. Complete lighting control.
            </h2>
            <p className="text-zinc-500 text-base md:text-lg mt-6 max-w-2xl leading-relaxed">
              Amaya Pro connects <span className="text-ink">luminaires, sensors, controls, network and software</span> into a single Network Lighting Control system — automated operation, centralized management, energy optimization and real-time monitoring across commercial, industrial and residential facilities.
            </p>
          </Reveal>
          <div className="mt-14">
            <SystemDiagram dark />
          </div>
        </div>
      </section>

      {/* 02 — CAPABILITIES */}
      <section className="bg-white border-t border-zinc-200 py-24 md:py-32" data-testid="section-capabilities">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <Chapter n="02">Core capabilities</Chapter>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight max-w-2xl leading-[1.05]">
              Seventeen ways the system works for you.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200 mt-14">
            {CAPABILITIES.map((c, i) => {
              const CapIcon = CAPABILITY_ICONS[c.name];
              return (
              <Reveal key={c.name} delay={(i % 4) * 0.06} className="bg-white">
                <div className="p-6 h-full hover:bg-bone transition-colors duration-300 group" data-testid={`capability-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <div className="flex items-center justify-between">
                    {CapIcon && <CapIcon size={20} className="text-signal" strokeWidth={1.6} />}
                    <span className="font-mono text-[10px] text-zinc-500/50">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <h3 className="font-display text-lg font-bold mt-3 group-hover:text-signal transition-colors">{c.name}</h3>
                  <p className="text-sm text-zinc-500 mt-2 leading-relaxed">{c.desc}</p>
                </div>
              </Reveal>
              );
            })}
            <Reveal delay={0.1} className="bg-white sm:col-span-2 lg:col-span-3">
              <Link to="/technology" className="p-6 h-full flex flex-col sm:flex-row items-center gap-6 hover:bg-bone transition-colors duration-300 group" data-testid="capability-mesh-panel">
                <div className="flex-1">
                  <p className="font-mono text-[10px] text-zinc-500/50 mb-2">SYS</p>
                  <h3 className="font-display text-2xl font-bold group-hover:text-signal transition-colors">One mesh. Every luminaire.</h3>
                  <p className="text-sm text-zinc-500 mt-2 leading-relaxed max-w-sm">
                    All seventeen capabilities run on a single Bluetooth Mesh network — watch data move between devices in real time.
                  </p>
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-signal mt-4 group-hover:gap-2.5 transition-all">
                    Explore the technology <ArrowRight size={13} />
                  </span>
                </div>
                <MeshPulse className="w-44 md:w-52 shrink-0" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 03 — WHY AMAYA */}
      <section className="bg-white border-t border-zinc-200 py-24 md:py-32" data-testid="section-why">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <Chapter n="03">Why Amaya</Chapter>
          </Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-14 items-start">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-14 mt-8">
            {WHY.map((w, i) => {
              const WhyIcon = WHY_ICONS[w.k];
              return (
              <Reveal key={w.k} delay={(i % 3) * 0.08}>
                <div className="border-t-2 border-signal pt-6" data-testid={`why-${w.k.toLowerCase()}`}>
                  {WhyIcon && <WhyIcon size={22} className="text-signal mb-3" strokeWidth={1.6} />}
                  <h3 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight">{w.k}</h3>
                  <p className="text-zinc-500 mt-3 leading-relaxed">{w.desc}</p>
                </div>
              </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.2} className="hidden lg:block sticky top-28">
            <div className="border border-zinc-200 bg-bone p-8">
              <SmartControl className="w-full h-auto" />
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 text-center mt-4">
                Tap. Dim. Harvest daylight.
              </p>
            </div>
          </Reveal>
          </div>
        </div>
      </section>

      {/* 04 — ECOSYSTEM (light) */}
      <section className="bg-bone text-ink py-24 md:py-32" data-testid="section-ecosystem">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="flex items-baseline gap-4 mb-4">
              <span className="font-mono text-sm text-signal">/04</span>
              <Overline dark={false}>The Amaya Pro Ecosystem</Overline>
            </div>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
              <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight max-w-2xl leading-[1.05]">
                Every device. One mesh.
              </h2>
              <Link to="/products" className="text-sm font-medium text-signal flex items-center gap-1.5 hover:gap-2.5 transition-all" data-testid="ecosystem-all-btn">
                Browse all 30 products <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
            {ECOSYSTEM.map((e, i) => {
              const rep = getById(products, e.ids[0]);
              if (!rep) return null;
              const EcoIcon = ECO_ICONS[e.cat];
              return (
                <Reveal key={e.cat} delay={(i % 3) * 0.08}>
                  <Link
                    to={`/products?category=${encodeURIComponent(e.cat)}`}
                    data-testid={`ecosystem-${e.cat.toLowerCase().replace(/ /g, "-")}`}
                    className="group block bg-white border border-zinc-200 hover:border-signal transition-colors h-full"
                  >
                    <div className="spotlight h-44 flex items-center justify-center overflow-hidden">
                      <img src={rep.image} alt={rep.name} loading="lazy" className="max-h-32 w-auto object-contain transition-transform duration-500 group-hover:scale-105" />
                    </div>
                    <div className="p-6">
                      <h3 className="font-display text-xl font-bold flex items-center justify-between">
                        <span className="flex items-center gap-2.5">{EcoIcon && <EcoIcon size={20} className="text-signal" strokeWidth={1.6} />} {e.label}</span>
                        <ArrowUpRight size={18} className="text-zinc-400 group-hover:text-signal transition-colors" />
                      </h3>
                      <p className="text-sm text-zinc-500 mt-2 leading-relaxed">{e.desc}</p>
                      <p className="font-mono text-[11px] text-zinc-400 mt-4">{e.ids.join(" · ")}</p>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
            {featured.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* 05 — TECHNOLOGY TEASER */}
      <section className="bg-white border-t border-zinc-200 py-24 md:py-32 relative overflow-hidden" data-testid="section-tech">
        <div className="absolute inset-0 grid-lines pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <Chapter n="05">Technology</Chapter>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05]">
              Bluetooth Mesh. Device to device to device.
            </h2>
            <p className="text-zinc-500 text-base md:text-lg mt-6 leading-relaxed max-w-lg">
              Bluetooth Mesh is a secure, low-power wireless protocol enabling many-to-many communication between lighting devices — scalable, reliable and resilient control across large facilities.
            </p>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link to="/technology" className="px-7 py-3.5 bg-signal text-white font-medium hover:bg-[#5c8f23] transition-colors flex items-center gap-2" data-testid="tech-explore-btn">
                Explore Technology <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="border border-zinc-200 bg-white p-8 shadow-sm">
              <div className="flex items-center justify-between gap-2" aria-label="Bluetooth Mesh many-to-many diagram">
                {[0, 1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex items-center gap-2 flex-1 last:flex-none">
                    <div className="w-10 h-10 border border-signal/60 bg-bone flex items-center justify-center shrink-0">
                      <span className="w-2 h-2 bg-signal rounded-full animate-pulse-dot" style={{ animationDelay: `${i * 0.35}s` }} />
                    </div>
                    {i < 4 && <div className="h-px flex-1 bg-gradient-to-r from-signal/60 to-zinc-200" />}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-px bg-zinc-200 border border-zinc-200 mt-8 text-center">
                {[["Protocol", "Bluetooth Mesh"], ["Topology", "Many-to-many"], ["Security", "PSA Level 3"]].map(([k, v]) => (
                  <div key={k} className="bg-white p-4">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">{k}</p>
                    <p className="font-display text-sm md:text-base font-bold mt-1">{v}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 06 — APPLICATIONS */}
      <section className="bg-white py-24 md:py-32" data-testid="section-applications">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <Chapter n="06">Applications</Chapter>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight max-w-2xl leading-[1.05]">
              Built for the spaces that work hardest.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200 mt-14">
            {APPLICATION_CARDS.slice(0, 4).map((a, i) => {
              const AppIcon = APP_ICONS[a.id];
              return (
              <Reveal key={a.id} delay={(i % 4) * 0.06} className="bg-white">
                <Link to={`/applications#${a.id}`} className="block p-6 h-full hover:bg-bone transition-colors group" data-testid={`app-card-${a.id}`}>
                  <div className="flex items-center justify-between">
                    {AppIcon && <AppIcon size={22} className="text-signal" strokeWidth={1.6} />}
                    <span className="font-mono text-[10px] text-zinc-500/50">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <h3 className="font-display text-xl font-bold mt-3 group-hover:text-signal transition-colors">{a.title}</h3>
                  <p className="text-sm text-zinc-500 mt-2 leading-relaxed line-clamp-3">{a.challenge}</p>
                  <span className="inline-flex items-center gap-1 text-xs text-signal mt-4">View application <ArrowUpRight size={12} /></span>
                </Link>
              </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 07 — RESOURCES / COMPLIANCE */}
      <section className="bg-bone text-ink border-t border-zinc-200 py-24 md:py-32" data-testid="section-resources">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-14">
          <Reveal>
            <div className="flex items-baseline gap-4 mb-4">
              <span className="font-mono text-sm text-signal">/07</span>
              <Overline dark={false}>Resources & Compliance</Overline>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05]">
              Research before you call.
            </h2>
            <p className="text-zinc-600 text-base md:text-lg mt-6 leading-relaxed max-w-lg">
              Specifications, wiring diagrams, dimensions, sensor coverage and listing details — organized, searchable and built for specifiers.
            </p>
            <Link to="/resources" className="inline-flex items-center gap-2 mt-8 px-7 py-3.5 bg-forest text-white font-medium hover:bg-signal transition-colors" data-testid="resources-btn">
              <Download size={16} /> Open Resource Library
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="space-y-4">
              <div className="bg-white border border-zinc-200 p-6 flex items-start gap-4">
                <BadgeCheck className="text-signal shrink-0 mt-0.5" size={22} />
                <div>
                  <h3 className="font-display text-lg font-bold">DLC QPL — Networked Lighting Controls</h3>
                  <p className="text-sm text-zinc-500 mt-1.5 leading-relaxed">
                    Listed with the DesignLights Consortium. Indoor: Product ID N-Q0TFLJ (qualified 2025-12-10). Outdoor: Product ID N-KGDT1F (qualified 2026-01-28).
                  </p>
                </div>
              </div>
              <div className="bg-white border border-zinc-200 p-6 flex items-start gap-4">
                <ShieldCheck className="text-signal shrink-0 mt-0.5" size={22} />
                <div>
                  <h3 className="font-display text-lg font-bold">PSA Level 3 security + 10-year warranty</h3>
                  <p className="text-sm text-zinc-500 mt-1.5 leading-relaxed">
                    PSA Level 3 security specified across Amaya Pro devices, backed by a 10-year in-house warranty with quick support and replacements.
                  </p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
